/* eslint-disable func-style, import/export */
import { ReactNode } from "react";
import { QueryResult, MutationFn, MutationResult } from "react-apollo";

type LoadingRenderCallback = () => ReactNode;
type FailedRenderCallback = (errorMessage: string) => ReactNode;

type QueryRenderCallback = (props: QueryResult) => ReactNode;
type QuerySuccessCallback = (props: QueryResult) => ReactNode;

type MutationRenderCallback = (
    mutationFn: MutationFn,
    props: MutationResult
) => ReactNode;
type MutationSuccessCallback = (
    props: MutationResult,
    mutationFn: MutationFn
) => ReactNode;

export function withApolloState(
    onDataReceived: QuerySuccessCallback,
    onLoading?: LoadingRenderCallback,
    onError?: FailedRenderCallback
): QueryRenderCallback;

export function withApolloState(
    onDataReceived: MutationSuccessCallback,
    onLoading?: LoadingRenderCallback,
    onError?: FailedRenderCallback
): MutationRenderCallback;

/**
 * @description
 * Universal wrapper for apollo Query return data
 * @param {Function} onDataReceived calls on data received
 * @param {Function} [onLoading] calls on loading state. If no function - return "Loading..."
 * @param {Function} [onError] calls on error. if no function - returns error message
 * @returns {Function} that is called by apollo's <Query>
 */
export function withApolloState(
    onDataReceived: MutationSuccessCallback | QuerySuccessCallback,
    onLoading: LoadingRenderCallback = () => "Loading...",
    onError: FailedRenderCallback = message => message
) {
    return (arg1: QueryResult | MutationFn, arg2?: MutationResult) => {
        const isMutation = typeof arg1 === "function";
        const props = (isMutation ? arg2 : arg1) as
            | QueryResult
            | MutationResult;
        const mutationFn = isMutation ? arg1 : undefined;
        if (props.loading) {
            return onLoading();
        } else if (props.error) {
            return onError(props.error.message);
        } else if (isMutation) {
            return (onDataReceived as MutationSuccessCallback)(
                props as MutationResult,
                mutationFn as MutationFn
            );
        } else {
            return (onDataReceived as QuerySuccessCallback)(
                props as QueryResult
            );
        }
    };
}

/* eslint-enable */
